import React, { useState, useEffect, useRef } from 'react';
import { RotateCcw, Home, Play } from 'lucide-react';

const ReflexGame = ({ config, onEnd, currentTheme }) => {
    const getBetterBg = (image, currentBg) => {
        if (image === "🫧") return "https://images.unsplash.com/photo-1519750783826-e2420f4d687f?auto=format&fit=crop&w=1200";
        if (image === "❤️") return "https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1200";
        return currentBg;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const safeConfig = config || {
        title: "Jeu de Réflexes",
        principle: "Touchez les éléments !",
        duration: 60,
        image: "🎈",
        bg: "https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&fit=crop&w=1000",
        speed: "slow",
        spawnRate: 2500,
        behavior: { direction: 'up', swing: 2, rotation: false }
    };

    const currentBg = getBetterBg(safeConfig.image, safeConfig.bg);
    const [objects, setObjects] = useState([]);
    const [score, setScore] = useState(0);
    const [gameActive, setGameActive] = useState(false);
    const [timeLeft, setTimeLeft] = useState(safeConfig.duration);
    const gameLoopRef = useRef();

    useEffect(() => {
        setTimeLeft(safeConfig.duration);
    }, [safeConfig.duration]);

    const startGame = () => {
        setGameActive(true);
        setScore(0);
        setObjects([]);
        setTimeLeft(safeConfig.duration);
    };

    useEffect(() => {
        if (!gameActive) return;

        const timer = setInterval(() => {
            setTimeLeft((t) => {
                if (t <= 1) {
                    setGameActive(false);
                    if (onEnd) onEnd(score);
                    return 0;
                }
                return t - 1;
            });
        }, 1000);

        const spawner = setInterval(() => {
            const id = Math.random().toString(36).substr(2, 9);
            const { direction } = safeConfig.behavior;
            let baseSpeed = safeConfig.speed === 'fast' ? 0.4 : safeConfig.speed === 'medium' ? 0.25 : 0.15;
            const velocity = baseSpeed + (Math.random() * 0.1);

            let x, y, vx, vy;
            const margin = 15;

            if (direction === 'side') {
                const fromLeft = Math.random() > 0.5;
                x = fromLeft ? -15 : 115;
                y = Math.random() * 60 + 20;
                vx = fromLeft ? velocity : -velocity;
                vy = (Math.random() - 0.5) * 0.02;
            } else if (direction === 'down') {
                x = Math.random() * (100 - 2 * margin) + margin;
                y = 115;
                vx = 0;
                vy = -velocity;
            } else {
                x = Math.random() * (100 - 2 * margin) + margin;
                y = -15;
                vx = 0;
                vy = velocity;
            }

            setObjects(prev => [...prev, {
                id, x, y, vx, vy, baseX: x, baseY: y,
                startTime: Date.now(),
                rotation: safeConfig.behavior.rotation ? Math.random() * 360 : 0,
                flip: vx > 0 ? -1 : 1
            }]);
        }, safeConfig.spawnRate || 2000);

        const update = () => {
            setObjects(prev => prev.map(obj => {
                const elapsed = (Date.now() - obj.startTime) / 1000;
                let newX = obj.x + obj.vx;
                let newY = obj.y + obj.vy;

                if (safeConfig.behavior.swing > 0) {
                    if (safeConfig.behavior.direction === 'side') {
                        newY = obj.baseY + (Math.sin(elapsed * 3) * safeConfig.behavior.swing);
                    } else {
                        newX = obj.baseX + (Math.sin(elapsed * 3) * safeConfig.behavior.swing);
                    }
                }

                return {
                    ...obj,
                    x: newX,
                    y: newY,
                    rotation: safeConfig.behavior.rotation ? obj.rotation + 0.8 : obj.rotation
                };
            }).filter(obj => obj.x > -25 && obj.x < 125 && obj.y > -25 && obj.y < 125));
            gameLoopRef.current = requestAnimationFrame(update);
        };
        update();

        return () => {
            clearInterval(timer);
            clearInterval(spawner);
            cancelAnimationFrame(gameLoopRef.current);
        };
    }, [gameActive, safeConfig, score, onEnd]);

    const handleClick = (id) => {
        setScore(s => s + 1);
        setObjects(prev => prev.filter(o => o.id !== id));
    };

    return (
        <div className="reflex-game-canvas">
            <div className="reflex-background" style={{ backgroundImage: `url(${currentBg})`, filter: gameActive ? 'none' : 'brightness(0.7) blur(2px)' }} />

            <div className="reflex-ui-overlay">
                <div className="reflex-score-pill">Score : {score}</div>
                <div className={`reflex-timer-pill ${timeLeft < 10 ? 'timer-low' : ''}`}>{timeLeft}s</div>
            </div>

            {!gameActive && (
                <div className="reflex-menu-overlay">
                    <h1 className="reflex-menu-title">
                        {timeLeft === 0 ? "Bien joué !" : safeConfig.title}
                    </h1>
                    <p className="reflex-menu-principle">
                        {timeLeft === 0 ? `Vous avez attrapé ${score} éléments.` : safeConfig.principle}
                    </p>
                    
                    <div className="reflex-menu-actions">
                        <button onClick={startGame} className="btn-reflex-start">
                            {timeLeft === 0 ? <RotateCcw size={32} /> : <Play size={32} fill="currentColor" />} 
                            {timeLeft === 0 ? "Rejouer" : "Commencer"}
                        </button>
                        {timeLeft === 0 && (
                            <button onClick={() => onEnd && onEnd(score)} className="btn-reflex-exit">
                                <Home size={32} /> Quitter
                            </button>
                        )}
                    </div>
                </div>
            )}

            {objects.map(obj => (
                <div
                    key={obj.id}
                    onMouseDown={() => handleClick(obj.id)}
                    onTouchStart={(e) => { e.preventDefault(); handleClick(obj.id); }}
                    className="reflex-object"
                    style={{
                        left: `${obj.x}%`,
                        bottom: `${obj.y}%`,
                        transform: `translate(-50%, 50%) rotate(${obj.rotation}deg) scaleX(${obj.flip})`
                    }}
                >
                    <span className="reflex-emoji">{safeConfig.image}</span>
                </div>
            ))}
        </div>
    );
};

export default ReflexGame;